// EditClientServiceSubscriptionTaskChecklistItem.jsx
import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { updateClientServiceSubscriptionTaskChecklistItem } from "../../../../services/clientServiceSubscriptionTaskChecklistItem/updateClientServiceSubscriptionTaskChecklistItem";
import LinkForm from "../../../clientServiceSubscription/links/LinkForm";
import ButtonModal from "../../updateClientServiceSubscriptionTaskModal/ButtonModal";
import InputTextModal from "../../modalGeneralComponents/InputTextModal";
import { usePermissions } from "../../../../context/PermissionsContext";

const LINK_NAME = "name";
const LINK_URL = "url";

const EditClientServiceSubscriptionTaskChecklistItem = ({
    updateChecklistItem,
    open,
    setOpen,
    setUpdateChecklistItem,
    loggedInEmployee,
    employees,
    getNewJobs,
}) => {
    const { can, permissionsLoading } = usePermissions();
    const canWriteSteps = can("steps.write");
    const [isSaving, setIsSaving] = useState(false);
    const [displayOptionalFields, setDisplayOptionalFields] = useState({
        notes: false,
        links: false,
    });
    const completeButtonRef = useRef(null);

    // Initialize link state
    const initialLink =
        updateChecklistItem.links && updateChecklistItem.links.length > 0
            ? updateChecklistItem.links[0]
            : { [LINK_NAME]: "", [LINK_URL]: "" };
    const [newLink, setNewLink] = useState(initialLink);

    const toggleLinks = () =>
        setDisplayOptionalFields((prev) => ({ ...prev, links: !prev.links }));

    const handleChangeLink = (e) => {
        const value = e.target.value;
        const linkObj = { [LINK_NAME]: value, [LINK_URL]: value };
        setNewLink(linkObj);
        setUpdateChecklistItem((prev) => ({
            ...prev,
            links: value.trim() ? [linkObj] : [],
        }));
    };

    const onHandleChange = (e) => {
        const { name, value } = e.target;
        setUpdateChecklistItem((prev) => ({ ...prev, [name]: value }));
    };

    // NEW: Map dropdown to boolean `isPermanent`
    const onHandleChangeRenewal = (e) => {
        const value = e.target.value; // "temporary" | "permanent"
        setUpdateChecklistItem((prev) => ({
            ...prev,
            isPermanent: value === "permanent",
        }));
    };

    const onHandleSubmit = async (e) => {
        e.preventDefault();
        if (permissionsLoading || !canWriteSteps || isSaving) return;

        // Optional guard, HTML required already covers this:
        if (
            updateChecklistItem.isPermanent === undefined ||
            updateChecklistItem.isPermanent === null
        ) {
            toast.error("Please choose Temporary or Permanent.");
            return;
        }

        setIsSaving(true);
        try {
            const response =
                await updateClientServiceSubscriptionTaskChecklistItem(
                    updateChecklistItem,
                );
            if (response.data.success) {
                toast.success(response.data.message);
                getNewJobs();
                setOpen(false);
            } else if (response.status !== 403) {
                toast.error(response.data.message);
            }
        } finally {
            setIsSaving(false);
        }
    };

    const onHandleCancel = () => setOpen(false);

    // Compute current select value from boolean; empty string will force selection if unset
    const renewalSelectValue =
        updateChecklistItem.isPermanent === undefined ||
        updateChecklistItem.isPermanent === null
            ? ""
            : updateChecklistItem.isPermanent
            ? "permanent"
            : "temporary";

    return (
        <AnimatePresence>
            {open && (
                <Dialog
                    initialFocus={completeButtonRef}
                    static
                    as={motion.div}
                    open={open}
                    onClose={onHandleCancel}
                    className="relative z-50"
                >
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    <div className="fixed inset-0 w-screen overflow-y-auto">
                        <div className="flex items-center justify-center min-h-full p-4">
                            <Dialog.Panel className="relative w-full max-w-lg p-6 overflow-hidden bg-white rounded-lg shadow-xl">
                                <Dialog.Title className="mb-4 text-lg font-medium text-center text-gray-900">
                                    Update Checklist Item
                                </Dialog.Title>

                                <form
                                    onSubmit={onHandleSubmit}
                                    className="space-y-5"
                                >
                                    {/* Description */}
                                    <InputTextModal
                                        required
                                        label="Description"
                                        name="description"
                                        onChange={onHandleChange}
                                        value={updateChecklistItem.description}
                                    />

                                    {/* REPLACED: Checkbox -> Dropdown (required) */}
                                    <div>
                                        <label className="block mb-1 text-sm font-medium text-gray-700">
                                            Keep item on renewal?{" "}
                                            <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            required
                                            name="keepOnRenewal"
                                            value={renewalSelectValue}
                                            onChange={onHandleChangeRenewal}
                                            className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                        >
                                            <option value="" disabled>
                                                Select an option
                                            </option>
                                            <option value="temporary">
                                                Temporary
                                            </option>
                                            <option value="permanent">
                                                Permanent
                                            </option>
                                        </select>
                                        <small className="block mt-1 text-gray-500">
                                            * Keeps the item on renewal if set to
                                            Permanent; Temporary will not persist.
                                        </small>
                                    </div>

                                    {/* Links Toggle */}
                                    {!displayOptionalFields.links ? (
                                        <div className="mt-4">
                                            <button
                                                onClick={toggleLinks}
                                                type="button"
                                                className="inline-flex items-center p-1 text-base text-white bg-teal-500 border border-transparent rounded-sm shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                            >
                                                Add link
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <LinkForm
                                                LINK_NAME={LINK_NAME}
                                                LINK_URL={LINK_URL}
                                                newLink={newLink}
                                                handleChange={handleChangeLink}
                                                inputLinksJSON={
                                                    updateChecklistItem.links
                                                }
                                                divWidth="w-full"
                                                title="Link"
                                                showName={false}
                                                allowMultiple={false}
                                            />
                                            <div className="mt-4">
                                                <button
                                                    onClick={toggleLinks}
                                                    type="button"
                                                    className="inline-flex items-center p-1 text-base text-white bg-red-500 border border-transparent rounded-sm shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                >
                                                    Hide link
                                                </button>
                                            </div>
                                        </>
                                    )}

                                    {/* Actions */}
                                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                        <ButtonModal
                                            name={isSaving ? "Saving..." : "Save"}
                                            type="submit"
                                            className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-teal-500 border border-transparent rounded-md shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0 sm:col-start-2 sm:text-sm"
                                            ref={completeButtonRef}
                                            disabled={
                                                isSaving ||
                                                permissionsLoading ||
                                                !canWriteSteps
                                            }
                                        />
                                        <ButtonModal
                                            name="Cancel"
                                            type="button"
                                            onClick={onHandleCancel}
                                            className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0 sm:mt-0 sm:col-start-1 sm:text-sm"
                                        />
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </div>
                    </div>
                </Dialog>
            )}
        </AnimatePresence>
    );
};

export default EditClientServiceSubscriptionTaskChecklistItem;
